import React, { useRef, useState } from 'react';
import useTodos from '../../hooks/useTodos';
import { useAuth } from '../../context/AuthContext';
import { todoService } from '../../services/todoService';
import InfoTooltip from '../InfoTooltip';
import './todo.css';

export default function TodoPanel() {
  const {
    todos,
    addTodo,
    toggleDone,
    loading,
    updateTitle,
    updatePriority,
    updateEstimate,
    updateDueAt,
    updateTags,
    addChecklistItem,
    toggleChecklistItem,
    removeChecklistItem,
    editChecklistItem,
    search,
    setSearch,
    priorityFilter,
    setPriorityFilter,
    tagFilter,
    setTagFilter,
    selected,
    toggleSelect,
    clearSelection,
    selectAllOpen,
    bulkComplete,
    bulkDelete,
    archiveTodo,
    onReorder,
  } = useTodos();
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('P2');
  const [estimate, setEstimate] = useState(0);
  const [tagsInput, setTagsInput] = useState('');
  const [dueAt, setDueAt] = useState('');
  const dragIdRef = useRef(null);
  const [newChecklistText, setNewChecklistText] = useState({});
  const [undo, setUndo] = useState(null); // { label, revert, timerId }
  const { user } = useAuth();
  const addDisabled = (!user) || !title.trim();
  const addDisabledReason = !user
    ? 'Sign in to add tasks'
    : loading
      ? 'Loading your tasks...'
      : !title.trim()
        ? 'Enter a task title'
        : '';

  const onAdd = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    await addTodo({ title: title.trim(), priority, estimatePomos: Number(estimate) || 0, tags, dueAt: dueAt || null });
    setTitle('');
    setPriority('P2');
    setEstimate(0);
    setTagsInput('');
    setDueAt('');
  };

  return (
    <div className="todos">
      {/* Add form */}
      <form className="todo-entry" onSubmit={onAdd}>
        <div style={{ display:'flex', alignItems:'center', flex:1 }}>
          <input
          type="text"
          placeholder="Add a task and hit Enter"
          title="Task title – keep it short and actionable (e.g., ‘Write intro paragraph’)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
          <InfoTooltip label="Task title – keep it short and actionable (e.g., ‘Write intro paragraph’)" />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} aria-label="Priority" title="Priority – P1 (urgent), P2 (normal), P3 (low)">
          <option value="P1">P1</option>
          <option value="P2">P2</option>
          <option value="P3">P3</option>
        </select>
          <InfoTooltip label="Priority – P1 (urgent), P2 (normal), P3 (low)" />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <input
          type="number"
          min="0"
          value={estimate}
          onChange={(e) => setEstimate(e.target.value)}
          placeholder="Est."
          aria-label="Estimate pomodoros"
          title="Estimated pomodoros – how many 25m focus blocks this task might take"
          style={{ width: 64 }}
        />
          <InfoTooltip label="Estimated pomodoros – how many 25m focus blocks this task might take" />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <input
          type="text"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          placeholder="tags, comma,separated"
          aria-label="Tags"
          title="Tags – comma-separated (e.g., study, math)"
        />
          <InfoTooltip label="Tags – comma-separated (e.g., study, math)" />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <input
          type="date"
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
          aria-label="Due date"
          title="Due date – optional deadline for this task"
        />
          <InfoTooltip label="Due date – optional deadline for this task" />
        </div>
        <button
          className="primary"
          type="submit"
          disabled={addDisabled}
          aria-disabled={addDisabled}
          title={addDisabled ? addDisabledReason : 'Add task'}
        >
          Add
        </button>
      </form>

      {/* Filters */}
      <div className="todo-filters" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input type="text" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} title="Search – filter tasks by title" />
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} title="Filter by priority">
          <option value="all">All priorities</option>
          <option value="P1">P1</option>
          <option value="P2">P2</option>
          <option value="P3">P3</option>
        </select>
        <input type="text" placeholder="Filter by tag" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} title="Filter by tag – type to match tags" />
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="todo-bulk" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <button
            className="ghost"
            onClick={async () => {
              const ids = Array.from(selected);
              const prev = ids
                .map((id) => todos.find((x) => x.id === id))
                .filter(Boolean)
                .map((t) => ({ id: t.id, status: t.status, completedAt: t.completedAt || null }));
              await bulkComplete();
              const timerId = setTimeout(() => setUndo(null), 7000);
              setUndo({
                label: `Completed ${prev.length} task(s)` ,
                timerId,
                revert: async () => {
                  if (!user) return;
                  await Promise.all(prev.map((p) => todoService.updateTodo(user.uid, p.id, { status: p.status, completedAt: p.completedAt })));
                },
              });
            }}
          >
            Complete Selected
          </button>
          <button
            className="ghost"
            onClick={async () => {
              const ids = Array.from(selected);
              const prev = ids
                .map((id) => todos.find((x) => x.id === id))
                .filter(Boolean)
                .map((t) => ({ id: t.id, status: t.status, completedAt: t.completedAt || null }));
              await bulkDelete();
              const timerId = setTimeout(() => setUndo(null), 7000);
              setUndo({
                label: `Archived ${prev.length} task(s)` ,
                timerId,
                revert: async () => {
                  if (!user) return;
                  await Promise.all(prev.map((p) => todoService.updateTodo(user.uid, p.id, { status: p.status, completedAt: p.completedAt })));
                },
              });
            }}
          >
            Delete Selected
          </button>
          <button className="ghost" onClick={clearSelection}>Clear Selection</button>
        </div>
      )}
      {selected.size === 0 && todos.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <button className="ghost" onClick={selectAllOpen}>Select All Open</button>
        </div>
      )}

      {/* List */}
      <ul className="todo-list">
        {todos.map((t, idx) => (
          <li
            key={t.id}
            className={`todo-item ${t.status === 'done' ? 'done' : ''}`}
            draggable
            onDragStart={() => (dragIdRef.current = t.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              const fromId = dragIdRef.current;
              if (!fromId || fromId === t.id) return;
              const ids = todos.map(x => x.id);
              const fromIdx = ids.indexOf(fromId);
              const toIdx = ids.indexOf(t.id);
              ids.splice(toIdx, 0, ids.splice(fromIdx, 1)[0]);
              onReorder(ids);
              dragIdRef.current = null;
            }}
            onTouchStart={(e) => (e.currentTarget.dataset.x = e.touches[0].clientX)}
            onTouchEnd={async (e) => {
              const startX = Number(e.currentTarget.dataset.x || 0);
              const endX = e.changedTouches[0].clientX;
              const dx = endX - startX;
              if (dx > 60) toggleDone(t); // swipe right complete
              if (dx < -60) {
                const prev = { id: t.id, status: t.status, completedAt: t.completedAt || null };
                await archiveTodo(t);
                const timerId = setTimeout(() => setUndo(null), 7000);
                setUndo({
                  label: `Archived 1 task` ,
                  timerId,
                  revert: async () => {
                    if (!user) return;
                    await todoService.updateTodo(user.uid, prev.id, { status: prev.status, completedAt: prev.completedAt });
                  },
                });
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <input type="checkbox" aria-label="Select for bulk" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} />
              <input type="checkbox" checked={t.status === 'done'} onChange={() => toggleDone(t)} aria-label="Mark done" />
              <input
                type="text"
                value={t.title}
                onChange={(e) => updateTitle(t, e.target.value)}
                className="inline"
                style={{ flex: 1 }}
              />
              <select value={t.priority} onChange={(e) => updatePriority(t, e.target.value)} aria-label="Priority">
                <option value="P1">P1</option>
                <option value="P2">P2</option>
                <option value="P3">P3</option>
              </select>
              <input type="number" min="0" value={t.estimatePomos || 0} onChange={(e) => updateEstimate(t, Number(e.target.value) || 0)} aria-label="Estimate" style={{ width: 56 }} />
              <span title="Actual pomodoros">⏱️ {t.actualPomos || 0}</span>
              <input type="text" value={(t.tags || []).join(', ')} onChange={(e) => updateTags(t, e.target.value.split(',').map(s => s.trim()).filter(Boolean))} aria-label="Tags" style={{ width: 140 }} className="inline" />
              <input type="date" value={t.dueAt ? new Date(t.dueAt.seconds ? t.dueAt.seconds * 1000 : t.dueAt).toISOString().slice(0,10) : ''} onChange={(e) => updateDueAt(t, e.target.value)} aria-label="Due date" />
            </div>
            {/* Checklist */}
            <div className="checklist">
              {(t.checklist || []).map((it) => (
                <div key={it.id} className="checklist-item">
                  <input type="checkbox" checked={!!it.done} onChange={() => toggleChecklistItem(t, it.id)} aria-label="Check subtask" />
                  <input
                    type="text"
                    value={it.text}
                    onChange={(e) => editChecklistItem(t, it.id, e.target.value)}
                    className="inline"
                    style={{ flex: 1 }}
                  />
                  <button className="ghost sm" onClick={() => removeChecklistItem(t, it.id)} aria-label="Remove subtask">✕</button>
                </div>
              ))}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const txt = newChecklistText[t.id] || '';
                  if (txt.trim()) addChecklistItem(t, txt.trim());
                  setNewChecklistText((s) => ({ ...s, [t.id]: '' }));
                }}
                className="checklist-add"
              >
                <input
                  type="text"
                  placeholder="Add subtask"
                  value={newChecklistText[t.id] || ''}
                  onChange={(e) => setNewChecklistText((s) => ({ ...s, [t.id]: e.target.value }))}
                />
                <button className="ghost sm" type="submit">Add</button>
              </form>
            </div>
          </li>
        ))}
        {!loading && todos.length === 0 && (
          <li className="empty">No tasks yet. Add your first one!</li>
        )}
      </ul>

      {undo && (
        <div className="undo-bar" role="status" aria-live="polite">
          <span>{undo.label}</span>
          <button
            className="ghost"
            onClick={() => {
              clearTimeout(undo.timerId);
              undo.revert();
              setUndo(null);
            }}
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
}
